import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Stack,
  IconButton,
  Collapse,
  CircularProgress
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import AnimatedLogo from '../components/common/AnimatedLogo';
import { useAuth } from '../context/AuthContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const TopicGenerator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topicSettings, setTopicSettings] = useState(() => {
    // Load saved settings from localStorage or use defaults
    const savedSettings = localStorage.getItem('topicSettings');
    return savedSettings ? JSON.parse(savedSettings) : {
      firstStep: 'Educational Content',
      secondStep: 'Building a personal brand',
      thirdStep: 'Professional',
      fourthStep: '',
      fifthStep: '',
      sixthStep: '',
      seventhStep: ''
    };
  });

  const [generatedTopics, setGeneratedTopics] = useState(() => {
    // Load saved topics from localStorage
    const savedTopics = localStorage.getItem('generatedTopics');
    const savedSettings = localStorage.getItem('lastGeneratedSettings');
    
    // Only return saved topics if settings match current settings
    if (savedTopics && savedSettings && savedSettings === JSON.stringify(topicSettings)) {
      return JSON.parse(savedTopics);
    }
    return [];
  });

  // Save settings whenever they change
  useEffect(() => {
    localStorage.setItem('topicSettings', JSON.stringify(topicSettings));
  }, [topicSettings]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [profDetailsLoading, setProfDetailsLoading] = useState(false);
  const [profDetailsSuccess, setProfDetailsSuccess] = useState('');
  const [professionalDetailsExpanded, setProfessionalDetailsExpanded] = useState(false);

  // Add this useEffect to fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        const userData = response.data.user;
        setUserProfile(userData);
        
        // Update topicSettings with professional details from user profile
        setTopicSettings(prev => ({
          ...prev,
          fourthStep: userData.industry || prev.fourthStep,
          fifthStep: userData.professionalRole || prev.fifthStep,
          sixthStep: userData.professionalRole || prev.sixthStep,
          seventhStep: userData.targetAudience || prev.seventhStep
        }));
        
      } catch (err) {
        console.error('Error fetching user profile:', err);
      }
    };

    fetchUserProfile();
  }, []);

  const handleGenerateTopics = async () => {
    if (!topicSettings.fourthStep || !topicSettings.fifthStep) {
      setError('Please fill in both Industry and Role fields');
      return;
    }

    // Check if we have cached results for these exact settings
    const savedSettings = localStorage.getItem('lastGeneratedSettings');
    const savedTopics = localStorage.getItem('generatedTopics');
    
    if (savedSettings === JSON.stringify(topicSettings) && savedTopics) {
      setGeneratedTopics(JSON.parse(savedTopics));
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      console.log('Generating topics with settings:', topicSettings);
      
      // Create the request payload
      const payload = {
        settings: {
          firstStep: [topicSettings.firstStep],
          secondStep: [topicSettings.secondStep],
          thirdStep: [topicSettings.thirdStep],
          fourthStep: [topicSettings.fourthStep],
          fifthStep: [topicSettings.fifthStep],
          sixthStep: topicSettings.sixthStep,
          seventhStep: topicSettings.seventhStep
        }
      };
      
      console.log('Sending payload:', payload);
      
      // Make API call to Gemini-powered backend
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/ai/topics`, 
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Received response:', response.data);
      
      // Handle the response
      let topics = [];
      
      if (Array.isArray(response.data)) {
        topics = response.data;
      } else if (response.data && Array.isArray(response.data.topics)) {
        topics = response.data.topics;
      } else if (response.data && response.data.content) {
        // Try to parse if content is a string
        try {
          const parsed = JSON.parse(response.data.content);
          if (Array.isArray(parsed)) {
            topics = parsed;
          }
        } catch (parseError) {
          console.error('Failed to parse content as JSON:', parseError);
          throw new Error('Invalid response format from server');
        }
      } else {
        console.error('Unexpected response format:', response.data);
        throw new Error('Invalid response format from server');
      }
      
      // Validate that we have valid topics
      const validTopics = topics.filter(item => 
        item && typeof item === 'object' && item.topic && typeof item.topic === 'string'
      );
      
      if (validTopics.length === 0) {
        throw new Error('No valid topics received from server');
      }
      
      console.log('Processed topics:', validTopics);
      setGeneratedTopics(validTopics);
      
      // Save the results and settings
      localStorage.setItem('generatedTopics', JSON.stringify(validTopics));
      localStorage.setItem('lastGeneratedSettings', JSON.stringify(topicSettings));
      
    } catch (error) {
      console.error('Topic generation error:', error);
      
      // Provide more specific error messages based on error type
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        
        console.error('API Error Response:', errorData);
        
        if (status === 401) {
          setError('Authentication failed. Please log in again.');
        } else if (status === 402) {
          setError('Insufficient credits. Please upgrade your plan.');
        } else if (status === 429) {
          setError('Too many requests. Please wait a moment and try again.');
        } else if (status === 503) {
          setError('Topic generation service is temporarily unavailable. Please try again later.');
        } else {
          setError(errorData?.message || `Server error (${status}). Please try again.`);
        }
      } else if (error.request) {
        console.error('Network Error:', error.request);
        setError('Network error. Please check your connection and try again.');
      } else {
        console.error('Unknown Error:', error.message);
        setError(error.message || 'Failed to generate topics. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTopic(null);
  };

  const handleNavigateToPost = () => {
    navigate('/dashboard/generate-post', { 
      state: { 
        topic: selectedTopic,
        autoFill: true
      } 
    });
    handleCloseDialog();
  };

  const handleNavigateToImage = () => {
    navigate('/dashboard/generate-image', { 
      state: { 
        topic: selectedTopic,
        autoFill: true
      } 
    });
    handleCloseDialog();
  };

  // Add a clear cache button
  const handleClearCache = () => {
    localStorage.removeItem('generatedTopics');
    localStorage.removeItem('lastGeneratedSettings');
    setGeneratedTopics([]);
  };

  // Add this function to save professional details
  const handleSaveProfessionalDetails = async () => {
    setProfDetailsLoading(true);
    setProfDetailsSuccess('');
    
    try {
      // Ensure no undefined values are sent to the server
      const industry = topicSettings.fourthStep || null;
      const professionalRole = topicSettings.fifthStep || null;
      const targetAudience = topicSettings.seventhStep || null;
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/auth/profile/professional`,
        {
          industry,
          professionalRole,
          targetAudience,
          linkedinProfile: userProfile?.linkedinProfile || null // Include this to match GeneratePost
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setProfDetailsSuccess('Professional details saved successfully!');
      setUserProfile(response.data.user);
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setProfDetailsSuccess('');
      }, 3000);
      
    } catch (error) {
      console.error('Error saving professional details:', error);
      setError('Failed to save professional details. Please try again.');
    } finally {
      setProfDetailsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>LinkedIn Topic Generator | Instation</title>
        <meta name="description" content="Generate engaging topics for your LinkedIn content with AI-powered suggestions." />
      </Helmet>
      
      <Container maxWidth="xl" className="page-container">
        <Box className="content-container">
          <Grid container spacing={3} className="content-grid">
            <Grid item xs={12} md={5} lg={4} className="content-panel">
              <Paper 
                elevation={0} 
                className="content-paper"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Typography 
                  variant="h6" 
                  className="component-heading"
                  sx={{ mb: 3, flexShrink: 0 }}
                >
                  Topic Settings
                </Typography>

                <Box sx={{
                  flex: 1, 
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pr: 2,
                  minHeight: 0
                }}>
                  <FormControl fullWidth size="small" sx={{ mb: { xs: 2, sm: 3 } }}>
                    <Typography 
                      variant="subtitle1" 
                      className="section-title"
                      style={{ fontWeight: 500, color: 'black' }}
                      sx={{ mb: 1 }}
                    >
                      Content Type
                    </Typography>
                    <Select
                      value={topicSettings.firstStep}
                      onChange={(e) => setTopicSettings(prev => ({ ...prev, firstStep: e.target.value }))}
                      sx={{ 
                        height: '56px',
                        borderRadius: 2,
                        '& .MuiSelect-select': {
                          padding: '16px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.1)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#4890fe',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#4890fe',
                        }
                      }}
                    >
                      {["Educational Content", "Industry News", "Case Studies", "Personal Achievements", "Career Tips"].map(type => (
                        <MenuItem 
                          key={type} 
                          value={type} 
                          sx={{ 
                            padding: '12px 16px',
                          }}
                        >
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" sx={{ mb: { xs: 2, sm: 3 } }}>
                    <Typography 
                      variant="subtitle1" 
                      className="section-title"
                      style={{ fontWeight: 500, color: 'black' }}
                      sx={{ mb: 1 }}
                    >
                      Purpose
                    </Typography>
                    <Select
                      value={topicSettings.secondStep}
                      onChange={(e) => setTopicSettings(prev => ({ ...prev, secondStep: e.target.value }))}
                      sx={{ 
                        height: '56px',
                        borderRadius: 2,
                        '& .MuiSelect-select': {
                          padding: '16px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.1)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#4890fe',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#4890fe',
                        }
                      }}
                    >
                      {["Networking", "Seeking new job opportunities", "Building a personal brand", "Generating leads", "Sharing professional expertise"].map(purpose => (
                        <MenuItem 
                          key={purpose} 
                          value={purpose} 
                          sx={{ 
                            padding: '12px 16px',
                          }}
                        >
                          {purpose}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" sx={{ mb: { xs: 2, sm: 3 } }}>
                    <Typography 
                      variant="subtitle1" 
                      className="section-title"
                      style={{ fontWeight: 500, color: 'black' }}
                      sx={{ mb: 1 }}
                    >
                      Tone
                    </Typography>
                    <Select
                      value={topicSettings.thirdStep}
                      onChange={(e) => setTopicSettings(prev => ({ ...prev, thirdStep: e.target.value }))}
                      sx={{ 
                        height: '56px',
                        borderRadius: 2,
                        '& .MuiSelect-select': {
                          padding: '16px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.1)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#4890fe',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#4890fe',
                        }
                      }}
                    >
                      {["Professional", "Casual", "Conversational", "Witty", "Persuasive", "Inspirational", "Analytical"].map(tone => (
                        <MenuItem 
                          key={tone} 
                          value={tone} 
                          sx={{ 
                            padding: '12px 16px',
                          }}
                        >
                          {tone}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box sx={{ mb: 3 }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        cursor: 'pointer',
                        mb: 2
                      }}
                      onClick={() => setProfessionalDetailsExpanded(!professionalDetailsExpanded)}
                    >
                      <Typography 
                        variant="subtitle1" 
                        className="section-title"
                        style={{ fontWeight: 500, color: 'black' }}
                      >
                        Additional Settings
                      </Typography>
                      {professionalDetailsExpanded ? (
                        <ExpandLessIcon sx={{ color: '#4A5568' }} />
                      ) : (
                        <ExpandMoreIcon sx={{ color: '#4A5568' }} />
                      )}
                    </Box>
                    
                    <Collapse in={professionalDetailsExpanded}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          These details will be saved to your profile and used for future content generation.
                        </Typography>
                        
                        <TextField
                          fullWidth
                          label="Industry"
                          placeholder="e.g., Design, Technology, Marketing..."
                          value={topicSettings.fourthStep}
                          onChange={(e) => setTopicSettings(prev => ({
                            ...prev,
                            fourthStep: e.target.value
                          }))}
                          sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: 'rgba(0, 0, 0, 0.1)',
                              },
                              '&:hover fieldset': {
                                borderColor: '#4890fe',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#4890fe',
                              }
                            }
                          }}
                        />

                        <TextField
                          fullWidth
                          label="Professional Role"
                          placeholder="e.g., Designer, Developer, Manager..."
                          value={topicSettings.fifthStep}
                          onChange={(e) => setTopicSettings(prev => ({
                            ...prev,
                            fifthStep: e.target.value,
                            sixthStep: e.target.value
                          }))}
                          sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: 'rgba(0, 0, 0, 0.1)',
                              },
                              '&:hover fieldset': {
                                borderColor: '#4890fe',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#4890fe',
                              }
                            }
                          }}
                        />
                        
                        <TextField
                          fullWidth
                          label="Target Audience"
                          placeholder="e.g., Startups, Enterprise businesses, Job seekers..."
                          value={topicSettings.seventhStep}
                          onChange={(e) => setTopicSettings(prev => ({
                            ...prev,
                            seventhStep: e.target.value
                          }))}
                          sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: 'rgba(0, 0, 0, 0.1)',
                              },
                              '&:hover fieldset': {
                                borderColor: '#4890fe',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#4890fe',
                              }
                            }
                          }}
                        />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveProfessionalDetails}
                            disabled={profDetailsLoading}
                            startIcon={profDetailsLoading ? <CircularProgress size={20} /> : null}
                            sx={{
                              bgcolor: '#4890fe',
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 600,
                              '&:hover': {
                                bgcolor: '#3a7ad9'
                              }
                            }}
                          >
                            {profDetailsLoading ? 'Saving...' : 'Save to Profile'}
                          </Button>
                        </Box>
                        
                        {profDetailsSuccess && (
                          <Alert severity="success" sx={{ mt: 2 }}>
                            {profDetailsSuccess}
                          </Alert>
                        )}
                      </Box>
                    </Collapse>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleGenerateTopics}
                    disabled={loading || !topicSettings.fourthStep || !topicSettings.fifthStep}
                    startIcon={loading ? <AutorenewIcon sx={{ animation: 'spin 1s linear infinite' }} /> : <AutorenewIcon />}
                    sx={{ 
                      height: 48,
                      borderRadius: 2,
                      textTransform: 'none',
                      bgcolor: '#4890fe',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: '#3a7ad9',
                      },
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  >
                    {loading ? 'Generating...' : 'Generate Topics'}
                  </Button>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7} lg={8} className="content-panel">
              <Paper 
                elevation={0} 
                className="content-paper"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                  pb: 2,
                  mb: 3,
                  flexShrink: 0
                }}>
                  <Typography 
                    variant="h6" 
                    className="component-heading"
                    sx={{ mb: 0 }}
                  >
                    Generated Topics
                  </Typography>
                  {generatedTopics.length > 0 && (
                    <Button
                      size="small"
                      onClick={handleClearCache}
                      sx={{ 
                        textTransform: 'none',
                        color: '#718096',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      Clear Results
                    </Button>
                  )}
                </Box>

                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 2,
                      borderRadius: 2,
                      '& .MuiAlert-message': {
                        fontWeight: 500
                      }
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <Box sx={{ 
                  flex: 1,
                  overflow: 'auto',
                  minHeight: 0,
                  pr: 2,
                }}>
                  {loading ? (
                    <Box sx={{ 
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column'
                    }}>
                      <AnimatedLogo size={100} color="#4890fe" />
                      <Typography variant="h6" sx={{ mt: 3, color: '#2D3748' }}>
                        Generating topics...
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This may take a few seconds
                      </Typography>
                    </Box>
                  ) : generatedTopics.length > 0 ? (
                    <Grid container spacing={2}>
                      {generatedTopics.map((item, index) => (
                        <Grid item xs={12} key={index}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => handleTopicSelect(item.topic)}
                            sx={{ 
                              p: 2,
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              textTransform: 'none',
                              borderRadius: 2,
                              color: '#2D3748',
                              borderColor: 'rgba(0, 0, 0, 0.1)',
                              backgroundColor: '#fff',
                              '&:hover': {
                                borderColor: '#4890fe',
                                backgroundColor: 'rgba(72, 144, 254, 0.04)',
                              }
                            }}
                          >
                            {item.topic}
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box sx={{ 
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                      border: '2px dashed rgba(0, 0, 0, 0.1)',
                      borderRadius: 2,
                      p: 3
                    }}>
                      <Box sx={{ 
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Box 
                          sx={{ 
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            bgcolor: 'rgba(72, 144, 254, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 3
                          }}
                        >
                          <AutorenewIcon sx={{ fontSize: 48, color: '#4890fe' }} />
                        </Box>
                        <Typography 
                          variant="h6"
                          sx={{ 
                            color: '#2D3748',
                            fontWeight: 600,
                            mb: 1
                          }}
                        >
                          Ready to generate topics?
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{ 
                            color: '#4A5568',
                            maxWidth: 400,
                            textAlign: 'center'
                          }}
                        >
                          Fill in the form on the left, then click<br />
                          "Generate Topics"
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Dialog 
            open={openDialog} 
            onClose={handleCloseDialog}
            PaperProps={{
              sx: {
                borderRadius: 2,
                maxWidth: '400px',
                width: '100%'
              }
            }}
          >
            <DialogTitle 
              sx={{ 
                pb: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography 
                variant="h6" 
                className="component-heading"
                sx={{ m: 0 }}
              >
                What would you like to generate?
              </Typography>
              <IconButton onClick={handleCloseDialog} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 3,
                  color: 'text.secondary',
                }}
              >
                Selected topic: {selectedTopic}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleNavigateToPost}
                sx={{
                  height: 48,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#4890fe',
                  '&:hover': {
                    bgcolor: '#3a7ad9',
                  }
                }}
              >
                Generate Post
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleNavigateToImage}
                sx={{
                  height: 48,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#4890fe',
                  color: '#4890fe',
                  '&:hover': {
                    borderColor: '#3a7ad9',
                    bgcolor: 'rgba(72, 144, 254, 0.04)',
                  }
                }}
              >
                Generate Image
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </>
  );
};

export default TopicGenerator; 